import { useState } from 'react';
import { Button, Text, Textarea } from '@telegram-apps/telegram-ui';

interface RatingModalProps {
  onSubmit: (rating: number, comment?: string) => void;
  onClose: () => void;
  isLoading: boolean;
}

/**
 * Модалка оценки качества работы (1-5 звёзд)
 * Появляется при нажатии "Принимаю работу"
 */
export function RatingModal({ onSubmit, onClose, isLoading }: RatingModalProps) {
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'flex-end',
        zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        style={{
          width: '100%',
          backgroundColor: 'var(--tgui--bg_color, #fff)',
          borderRadius: '16px 16px 0 0',
          padding: '24px 20px',
          maxHeight: '80vh',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <Text weight="1" style={{ fontSize: '18px', textAlign: 'center', marginBottom: '8px' }}>
          Оцените качество работы
        </Text>
        <Text style={{ fontSize: '14px', textAlign: 'center', color: '#999', marginBottom: '20px' }}>
          Ваша оценка поможет нам стать лучше
        </Text>

        {/* Stars */}
        <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginBottom: '20px' }}>
          {[1, 2, 3, 4, 5].map((star) => (
            <span
              key={star}
              onClick={() => setRating(star)}
              style={{
                fontSize: '40px',
                cursor: 'pointer',
                color: star <= rating ? '#FFB800' : '#444',
                transition: 'color 0.15s, transform 0.15s',
                transform: star <= rating ? 'scale(1.1)' : 'scale(1)',
                userSelect: 'none',
              }}
            >
              {star <= rating ? '\u2605' : '\u2606'}
            </span>
          ))}
        </div>

        {/* Rating label */}
        {rating > 0 && (
          <Text style={{
            fontSize: '14px',
            textAlign: 'center',
            marginBottom: '16px',
            color: rating >= 4 ? '#34C759' : rating >= 2 ? '#FF9500' : '#FF3B30',
          }}>
            {rating === 5 && 'Отлично!'}
            {rating === 4 && 'Хорошо'}
            {rating === 3 && 'Удовлетворительно'}
            {rating === 2 && 'Плохо'}
            {rating === 1 && 'Очень плохо'}
          </Text>
        )}

        {/* Comment (optional) */}
        <Textarea
          placeholder="Комментарий (необязательно)"
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          style={{ minHeight: '80px', marginBottom: '16px' }}
        />

        {/* Buttons */}
        <div style={{ display: 'flex', gap: '8px' }}>
          <Button
            size="l"
            stretched
            onClick={() => onSubmit(rating, comment.trim() || undefined)}
            loading={isLoading}
            disabled={rating === 0 || isLoading}
          >
            Принять работу
          </Button>
          <Button
            size="l"
            mode="outline"
            onClick={onClose}
            disabled={isLoading}
          >
            Отмена
          </Button>
        </div>
      </div>
    </div>
  );
}

interface RatingThankYouProps {
  rating: number;
  onClose: () => void;
}

/**
 * Экран благодарности после оценки
 * 4-5 звёзд: ссылки на отзывы
 * 0-3 звезды: менеджер свяжется
 */
export function RatingThankYou({ rating, onClose }: RatingThankYouProps) {
  const REVIEW_2GIS = 'https://go.2gis.com/7K96Z';
  const REVIEW_YANDEX = 'https://yandex.ru/maps/-/CPBKzV60';

  const isPositive = rating >= 4;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        style={{
          width: 'calc(100% - 40px)',
          maxWidth: '360px',
          backgroundColor: 'var(--tgui--bg_color, #fff)',
          borderRadius: '16px',
          padding: '32px 24px',
          textAlign: 'center',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ fontSize: '48px', marginBottom: '16px' }}>
          {isPositive ? '\u2728' : '\uD83D\uDCDD'}
        </div>

        <Text weight="1" style={{ fontSize: '20px', marginBottom: '8px' }}>
          {isPositive ? 'Спасибо за оценку!' : 'Спасибо за обратную связь'}
        </Text>

        <Text style={{ fontSize: '14px', color: '#999', marginBottom: '24px' }}>
          {isPositive
            ? 'Мы рады, что вам понравилось! Будем благодарны за отзыв.'
            : 'Менеджер свяжется с вами для решения вопроса.'}
        </Text>

        {isPositive && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '20px' }}>
            <a
              href={REVIEW_2GIS}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'block',
                padding: '12px',
                backgroundColor: '#34C759',
                color: '#fff',
                borderRadius: '10px',
                textDecoration: 'none',
                fontSize: '15px',
                fontWeight: '600',
              }}
            >
              Оставить отзыв на 2ГИС
            </a>
            <a
              href={REVIEW_YANDEX}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'block',
                padding: '12px',
                backgroundColor: '#FC3F1D',
                color: '#fff',
                borderRadius: '10px',
                textDecoration: 'none',
                fontSize: '15px',
                fontWeight: '600',
              }}
            >
              Оставить отзыв на Яндекс.Картах
            </a>
          </div>
        )}

        <Button
          size="l"
          stretched
          mode={isPositive ? 'outline' : 'filled'}
          onClick={onClose}
        >
          Закрыть
        </Button>
      </div>
    </div>
  );
}
